/**
 * Builds the seed maps handed to `enterEditMode` when a user mutates a
 * saved scenario.
 *
 * Entering edit mode replaces the planning-mode draft wholesale, and the
 * subsequent `updateScenario` re-sends EVERY draft action. So any action
 * the seed omits — or seeds without its persisted financial details — is
 * re-sent with empty financials and silently overwrites what was stored
 * (SE-5567). Every call site that auto-enters edit mode must therefore
 * seed from the scenario's actual persisted state; this hook is the single
 * place that fetch-plus-derive lives.
 * @module features/squad-planning/hooks/useScenarioEditSeed
 */

import { useMemo } from "react";

import type { TargetPositionPlayerModel } from "@/generated/graphql";
import { useSquadPlanningGetScenarioQuery } from "@/generated/graphql";

import type { SignedPlayerData } from "@/features/squad-planning/stores/scenarioStore";
import type {
  ActionDetails,
  PlayerAction,
  SquadPlayer,
} from "@/features/squad-planning/types";
import {
  buildActionsMapFromPlayers,
  buildActionsMapFromScenario,
  buildScenarioDetailsMap,
  buildScenarioSignedPlayersMap,
} from "@/features/squad-planning/utils";

/** The three maps `enterEditMode` takes, derived from the saved scenario. */
export interface ScenarioEditSeed {
  /**
   * Player id → action. Union of the scenario's OWN persisted actions, the
   * roster's `transitionType`, and raw targets' `transitionType` (SE-8346),
   * because externally signed players exist only as scenario actions — a
   * roster-only map would silently drop a previously-signed external
   * player, roster or target, on the next update.
   */
  readonly actionsMap: Readonly<Record<string, PlayerAction>>;
  /** Player id → persisted financial details for that action. */
  readonly detailsMap: Readonly<Record<string, ActionDetails>>;
  /** Player id → external-player data for persisted Sign actions. */
  readonly signedPlayersMap: Readonly<Record<string, SignedPlayerData>>;
  /**
   * False while the saved scenario is still being fetched. Callers MUST NOT
   * seed edit mode with an unresolved seed — the maps are empty at that
   * point, which is precisely the SE-5567 shape.
   */
  readonly isReady: boolean;
}

/**
 * Derives the `enterEditMode` seed for the active saved scenario.
 * @param players - Roster players carrying `transitionType`
 * @param rawTargets - Raw target rows carrying `id`/`transitionType` (SE-8346
 *   — `useSquadPlanningPlayers().players` no longer includes Targets; a
 *   target signed via a persisted scenario action still needs its
 *   `transitionType` here, same as before, just sourced from the raw rows
 *   instead of a fully-formatted Targets tier)
 * @param activeId - The active scenario id, or null when none is active
 * @param isPlanningModeActive - Whether a draft is already in flight
 * @returns The actions, details and signed-player maps to seed edit mode with
 */
export function useScenarioEditSeed(
  players: readonly SquadPlayer[],
  rawTargets: readonly TargetPositionPlayerModel[],
  activeId: string | null,
  isPlanningModeActive: boolean
): ScenarioEditSeed {
  // Served from the Apollo cache — the surfaces that render a saved
  // scenario have already fetched it by the time a user can act on it.
  const isViewingSaved = !isPlanningModeActive && activeId != null;
  const { data: scenarioData } = useSquadPlanningGetScenarioQuery({
    variables: { scenarioId: activeId ?? "" },
    skip: !isViewingSaved,
    fetchPolicy: "cache-first",
  });

  const actions = scenarioData?.getScenario?.actions;

  // The scenario's own actions are the base (they cover external signings
  // absent from the roster); the roster's transitionType overlays on top so
  // in-flight roster state still wins where both describe the same player.
  // rawTargets (SE-8346) covers the same overlay for targets, without
  // requiring them formatted — just the two fields buildActionsMapFromPlayers
  // reads, with transitionType normalised off the raw enum/undefined shape.
  const actionsMap = useMemo(
    () => ({
      ...buildActionsMapFromScenario(actions ?? []),
      ...buildActionsMapFromPlayers(players),
      ...buildActionsMapFromPlayers(
        rawTargets.map(t => ({
          id: t.id,
          transitionType: t.transitionType ?? null,
        }))
      ),
    }),
    [actions, players, rawTargets]
  );
  const detailsMap = useMemo(() => buildScenarioDetailsMap(actions), [actions]);
  const signedPlayersMap = useMemo(
    () => buildScenarioSignedPlayersMap(actions),
    [actions]
  );

  // Nothing to wait for unless we actually need the saved scenario.
  const isReady = !isViewingSaved || scenarioData?.getScenario != null;

  return { actionsMap, detailsMap, signedPlayersMap, isReady };
}
